import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import AdminForm from '../../components/admin/AdminForm';
import tourApi, { registrationApi } from '../../services/tourApi';
import { tourStopsAPI, registrationsAPI } from '../../services/api';
import { TourStop } from '../../data/tourStops';
import { Calendar, MapPin, Users, Trophy, Star, Mail, Phone, User, Download } from 'lucide-react';

const AdminTour = () => {
  const navigate = useNavigate();
  const [tourStops, setTourStops] = useState<TourStop[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<TourStop | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | 'add'>('view');
  const [activeTab, setActiveTab] = useState<'events' | 'registrations'>('events');

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Utiliser les nouvelles APIs
      const [eventsResponse, registrationsResponse] = await Promise.all([
        tourStopsAPI.getAll(),
        registrationsAPI.getAll()
      ]);
      
      // Transformer les données des événements
      const eventsData = eventsResponse.records.map((stop: any) => ({
        ...stop,
        highlights_fr: typeof stop.highlights_fr === 'string' 
          ? stop.highlights_fr.split(',').map((h: string) => h.trim())
          : stop.highlights_fr || [],
        highlights_en: typeof stop.highlights_en === 'string'
          ? stop.highlights_en.split(',').map((h: string) => h.trim())
          : stop.highlights_en || []
      }));
      
      setTourStops(eventsData);
      setRegistrations(registrationsResponse.records);
    } catch (error) {
      console.error('Error fetching data:', error);
      
      // Fallback vers les anciennes APIs en cas d'erreur
      try {
        const [eventsData, registrationsData] = await Promise.all([
          tourApi.getTourStops(),
          registrationApi.getRegistrations()
        ]);
        
        setTourStops(eventsData);
        setRegistrations(registrationsData);
      } catch (fallbackError) {
        console.error('Fallback API also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    // Prepare data for Excel
    const exportData = registrations.map(reg => ({
      'Confirmation Number': reg.confirmationNumber,
      'First Name': reg.firstName,
      'Last Name': reg.lastName,
      'Email': reg.email,
      'Phone': reg.phone,
      'Country': reg.country,
      'Category': reg.category,
      'Experience Level': reg.experience,
      'Registration Date': reg.submittedAt
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Registrations');

    // Generate Excel file
    XLSX.writeFile(wb, `tour_registrations_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setViewMode('add');
    setShowModal(true);
  };

  const handleEditEvent = (event: TourStop) => {
    setEditingEvent(event);
    setViewMode('edit');
    setShowModal(true);
  };

  const handleViewEvent = (event: TourStop) => {
    setEditingEvent(event);
    setViewMode('view');
    setShowModal(true);
  };

  const handleDeleteEvent = async (event: TourStop) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'événement ${event.location}, ${event.country} ?`)) {
      try {
        // Appel à l'API de suppression
        await tourStopsAPI.delete(event.id);
        
        setTourStops(prev => prev.filter(item => item.id !== event.id));
        alert('Événement supprimé avec succès');
      } catch (error) {
        console.error('Error deleting event:', error);
        
        // Fallback : suppression locale en cas d'erreur API
        setTourStops(prev => prev.filter(item => item.id !== event.id));
        alert('Événement supprimé localement (erreur API)');
      }
    }
  };

  const handleSubmitEvent = async (formData: any) => {
    try {
      setFormLoading(true);
      
      // Traitement des highlights (conversion string vers array)
      const processedData = {
        ...formData,
        highlights_fr: formData.highlights_fr ? formData.highlights_fr.split(',').map((h: string) => h.trim()) : [],
        highlights_en: formData.highlights_en ? formData.highlights_en.split(',').map((h: string) => h.trim()) : [],
        participants: parseInt(formData.participants) || 0,
        year: parseInt(formData.year) || new Date().getFullYear()
      };

      let apiSuccess = false;
      
      if (editingEvent) {
        try {
          // Appel API pour mise à jour
          await tourStopsAPI.update({ id: editingEvent.id, ...processedData });
          apiSuccess = true;
        } catch (error) {
          console.error('API update failed:', error);
        }
        
        // Mise à jour locale (que l'API ait réussi ou non)
        const updatedEvent = { ...editingEvent, ...processedData };
        setTourStops(prev => prev.map(item => 
          item.id === editingEvent.id ? updatedEvent : item
        ));
      } else {
        try {
          // Appel API pour création
          const response = await tourStopsAPI.create(processedData);
          
          if (response.id) {
            const newEvent: TourStop = {
              id: response.id,
              ...processedData,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              status: 'upcoming' as const
            };
            setTourStops(prev => [newEvent, ...prev]);
            apiSuccess = true;
          }
        } catch (error) {
          console.error('API create failed:', error);
        }
        
        if (!apiSuccess) {
          // Fallback : création locale
          const newEvent: TourStop = {
            id: Math.max(0, ...tourStops.map(item => item.id)) + 1,
            ...processedData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            status: 'upcoming' as const
          };
          setTourStops(prev => [newEvent, ...prev]);
        }
      }
      
      setShowModal(false);
      setEditingEvent(null);
      alert(apiSuccess ? 'Événement enregistré avec succès' : 'Événement enregistré localement (erreur API)');
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Erreur lors de l\'enregistrement de l\'événement');
    } finally {
      setFormLoading(false);
    }
  };

  const toggleFeatured = async (event: TourStop) => {
    try {
      const newStatus = event.status === 'featured' ? 'upcoming' : 'featured';
      
      // Appel API pour changer le statut
      if (newStatus === 'featured') {
        await tourStopsAPI.setFeatured(event.id);
      } else {
        await tourStopsAPI.removeFeatured(event.id);
      }
      
      // D'abord, retirer le statut featured de tous les autres événements
      const updatedEvents = tourStops.map(e => ({
        ...e,
        status: e.id === event.id 
          ? newStatus
          : (e.status === 'featured' ? 'upcoming' : e.status)
      }));
      
      setTourStops(updatedEvents);
    } catch (error) {
      console.error('Error updating featured status:', error);
      
      // Fallback : mise à jour locale en cas d'erreur API
      const updatedEvents = tourStops.map(e => ({
        ...e,
        status: e.id === event.id 
          ? (e.status === 'featured' ? 'upcoming' : 'featured')
          : (e.status === 'featured' ? 'upcoming' : e.status)
      }));
      
      setTourStops(updatedEvents);
      alert('Statut mis à jour localement (erreur API)');
    }
  };

  const eventFormFields = [
    {
      name: 'country',
      label: 'Pays',
      type: 'text' as const,
      required: true,
      placeholder: 'Nom du pays'
    },
    {
      name: 'location',
      label: 'Ville/Lieu',
      type: 'text' as const,
      required: true,
      placeholder: 'Nom de la ville ou du lieu'
    },
    {
      name: 'dates',
      label: 'Dates',
      type: 'text' as const,
      required: true,
      placeholder: '15-20 Mai 2024'
    },
    {
      name: 'month',
      label: 'Mois',
      type: 'text' as const,
      required: true,
      placeholder: 'Mai'
    },
    {
      name: 'year',
      label: 'Année',
      type: 'number' as const,
      required: true,
      placeholder: '2024'
    },
    {
      name: 'image_url',
      label: 'URL de l\'image',
      type: 'text' as const,
      placeholder: 'https://example.com/image.jpg'
    },
    {
      name: 'spot',
      label: 'Spot de surf',
      type: 'text' as const,
      placeholder: 'Nom du spot'
    },
    {
      name: 'participants',
      label: 'Nombre de participants',
      type: 'number' as const,
      placeholder: '32'
    },
    {
      name: 'prize_pool',
      label: 'Prize Pool',
      type: 'text' as const,
      placeholder: '50,000€'
    },
    {
      name: 'winner_men',
      label: 'Vainqueur Hommes',
      type: 'text' as const,
      placeholder: 'Nom du vainqueur'
    },
    {
      name: 'winner_women',
      label: 'Vainqueur Femmes',
      type: 'text' as const,
      placeholder: 'Nom de la vainqueur'
    },
    {
      name: 'description_fr',
      label: 'Description (Français)',
      type: 'textarea' as const,
      placeholder: 'Description de l\'événement en français',
      rows: 3
    },
    {
      name: 'description_en',
      label: 'Description (Anglais)',
      type: 'textarea' as const,
      placeholder: 'Description de l\'événement en anglais',
      rows: 3
    },
    {
      name: 'highlights_fr',
      label: 'Points forts (Français)',
      type: 'textarea' as const,
      placeholder: 'Point1,Point2,Point3 (séparés par des virgules)',
      rows: 3
    },
    {
      name: 'highlights_en',
      label: 'Points forts (Anglais)',
      type: 'textarea' as const,
      placeholder: 'Highlight1,Highlight2,Highlight3 (separated by commas)',
      rows: 3
    }
  ];

  const eventColumns = [
    {
      key: 'location',
      label: 'Lieu',
      sortable: true,
      render: (value: string, row: TourStop) => (
        <div className="flex items-center">
          <div>
            <div className="font-medium text-gray-900">{value}, {row.country}</div>
            <div className="text-sm text-gray-500">{row.spot}</div>
          </div>
        </div>
      )
    },
    {
      key: 'dates',
      label: 'Dates',
      sortable: true,
      render: (value: string, row: TourStop) => (
        <div>
          <div className="font-medium">{value}</div>
          <div className="text-sm text-gray-500">{row.month} {row.year}</div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value: string, row: TourStop) => (
        <div className="flex items-center space-x-2">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            value === 'featured' ? 'bg-yellow-100 text-yellow-800' :
            value === 'completed' ? 'bg-green-100 text-green-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {value === 'featured' ? 'À la une' :
             value === 'completed' ? 'Terminé' : 'À venir'}
          </span>
          {value !== 'completed' && (
            <button
              onClick={() => toggleFeatured(row)}
              className={`p-1 rounded ${
                value === 'featured' ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'
              }`}
              title={value === 'featured' ? 'Retirer de la une' : 'Mettre à la une'}
            >
              <Star className={`h-4 w-4 ${value === 'featured' ? 'fill-current' : ''}`} />
            </button>
          )}
        </div>
      )
    },
    {
      key: 'participants',
      label: 'Participants',
      sortable: true
    },
    {
      key: 'prize_pool',
      label: 'Prize Pool',
      sortable: true
    },
    {
      key: 'winner_men',
      label: 'Vainqueur',
      render: (value: string) => value || 'TBD'
    }
  ];

  const registrationColumns = [
    {
      key: 'confirmationNumber',
      label: 'N° Confirmation',
      sortable: true,
      render: (value: string) => (
        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{value} </span>
      )
    },
    {
      key: 'firstName',
      label: 'Participant',
      render: (value: string, row: any) => (
        <div>
          <div className="font-medium">{value} {row.lastName}</div>
          <div className="text-sm text-gray-500">{row.email}</div>
        </div>
      )
    },
    {
      key: 'country',
      label: 'Pays',
      sortable: true
    },
    {
      key: 'category',
      label: 'Catégorie',
      render: (value: string) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    },
    {
      key: 'phone',
      label: 'N° téléphone',
      sortable: true,
      render: (value: string) => (
        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{value} </span>
      )
    },
    {
      key: 'experience',
      label: 'Niveau',
      render: (value: string) => {
        const colors = {
          'debutant': 'bg-green-100 text-green-800',
          'intermediaire': 'bg-yellow-100 text-yellow-800',
          'avancée': 'bg-orange-100 text-orange-800',
          'professionnelle': 'bg-red-100 text-red-800'
        };
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        );
      }
    },
    {
      key: 'submittedAt',
      label: 'Date inscription',
      render: (value: string) => (value)
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Gestion du Tour</h1>
          <p className="text-gray-600">Gérez les événements du tour et les inscriptions</p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('events')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'events'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Événements ({tourStops.length})
              </button>
              <button
                onClick={() => setActiveTab('registrations')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'registrations'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Inscriptions ({registrations.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'events' ? (
          <AdminTable
            title="Événements du Tour"
            data={tourStops}
            columns={eventColumns}
            onAdd={handleAddEvent}
            onEdit={handleEditEvent}
            onView={handleViewEvent}
            onDelete={handleDeleteEvent}
            searchable
            filterable
            selectable
            pagination
            itemsPerPage={10}
          />
        ) : (
          <div>
            <div className="flex justify-end mb-4">
              <button
                onClick={handleExportToExcel}
                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                Exporter en Excel
              </button>
            </div>
            <AdminTable
              title="Inscriptions"
              data={registrations}
              columns={registrationColumns}
              searchable
              filterable
              pagination
              itemsPerPage={15}
            />
          </div>
        )}

        {/* Event Modal */}
        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingEvent(null);
          }}
          title={
            viewMode === 'add' 
              ? 'Nouvel événement' 
              : viewMode === 'edit' 
                ? 'Modifier l\'événement' 
                : 'Détails de l\'événement'
          }
          size="xl"
        >
          {viewMode === 'view' && editingEvent ? (
            <div className="p-6 space-y-6">
              <div className="flex items-start space-x-6 mb-6">
                <img 
                  src={editingEvent.image_url} 
                  alt={`${editingEvent.location}, ${editingEvent.country}`}
                  className="w-32 h-32 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {editingEvent.location}, {editingEvent.country}
                  </h3>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{editingEvent.dates}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin className="h-4 w-4" />
                      <span>{editingEvent.spot}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>{editingEvent.participants} participants</span>
                    </div>
                  </div>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    editingEvent.status === 'featured' ? 'bg-yellow-100 text-yellow-800' :
                    editingEvent.status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {editingEvent.status === 'featured' ? 'À la une' :
                     editingEvent.status === 'completed' ? 'Terminé' : 'À venir'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Prize Pool</h4>
                  <p className="text-gray-800">{editingEvent.prize_pool}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Participants</h4>
                  <p className="text-gray-800">{editingEvent.participants}</p>
                </div>
              </div>

              {(editingEvent.winner_men || editingEvent.winner_women) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {editingEvent.winner_men && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Vainqueur Hommes</h4>
                      <div className="flex items-center space-x-2">
                        <Trophy className="h-5 w-5 text-yellow-500" />
                        <span className="text-gray-800">{editingEvent.winner_men}</span>
                      </div>
                    </div>
                  )}
                  {editingEvent.winner_women && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Vainqueur Femmes</h4>
                      <div className="flex items-center space-x-2">
                        <Trophy className="h-5 w-5 text-yellow-500" />
                        <span className="text-gray-800">{editingEvent.winner_women}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Description (Français)</h4>
                <p className="text-gray-800 bg-gray-50 p-4 rounded-lg">{editingEvent.description_fr}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Description (Anglais)</h4>
                <p className="text-gray-800 bg-gray-50 p-4 rounded-lg">{editingEvent.description_en}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Points forts (Français)</h4>
                <ul className="list-disc pl-5 space-y-1">
                  {editingEvent.highlights_fr.map((highlight, index) => (
                    <li key={index} className="text-gray-800">{highlight}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Points forts (Anglais)</h4>
                <ul className="list-disc pl-5 space-y-1">
                  {editingEvent.highlights_en.map((highlight, index) => (
                    <li key={index} className="text-gray-800">{highlight}</li>
                  ))}
                </ul>
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setViewMode('edit')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingEvent(null);
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            <AdminForm
              fields={eventFormFields}
              initialData={editingEvent ? {
                ...editingEvent,
                highlights_fr: editingEvent.highlights_fr.join(', '),
                highlights_en: editingEvent.highlights_en.join(', ')
              } : {}}
              onSubmit={handleSubmitEvent}
              onCancel={() => {
                setShowModal(false);
                setEditingEvent(null);
              }}
              loading={formLoading}
            />
          )}
        </AdminModal>
      </div>
    </div>
  );
};

export default AdminTour;